/**
 * Pure utility functions for message processing.
 * Extracted from the original monolithic component — no React dependencies.
 */

// Linkify bare URLs in a text segment (no markdown links present)
const linkifyUrls = (segment) => {
  const urlRegex =
    /(https?:\/\/(?:[-\w.])+(?::[0-9]+)?(?:\/(?:[\w/_.%-])*)?(?:\?(?:[\w&=%.+-])*)?(?:#(?:[\w.%-])*)?)/g;

  return segment.replace(urlRegex, (url) => {
    const cleanUrl = url.replace(/[.,;:!?)]$/, "");
    const trailing = url.slice(cleanUrl.length);
    return `[${cleanUrl}](${cleanUrl})${trailing}`;
  });
};

// Function to convert plain URLs to Markdown format to ensure proper rendering.
// Preserves existing markdown links and only linkifies bare URLs.
export const convertUrlsToMarkdown = (text) => {
  if (!text || typeof text !== "string") return text;

  // Split around existing markdown links, process only non-link segments
  const markdownLinkRegex = /\[([^\]]*)\]\(([^)]+)\)/g;

  let result = "";
  let lastIndex = 0;
  let match;

  while ((match = markdownLinkRegex.exec(text)) !== null) {
    // Linkify bare URLs in the segment before this markdown link
    result += linkifyUrls(text.slice(lastIndex, match.index));
    // Keep the existing markdown link as-is
    result += match[0];
    lastIndex = match.index + match[0].length;
  }

  // Linkify remaining text after the last markdown link
  result += linkifyUrls(text.slice(lastIndex));

  return result;
};

// Helper function to check if a URL points to an image file
export const isImageUrl = (url) => {
  if (!url || typeof url !== "string") return false;

  const imageExtensions =
    /\.(jpg|jpeg|png|gif|webp|bmp|svg|ico|tiff|tif|avif)([?#].*)?$/i;

  if (imageExtensions.test(url)) return true;

  const imageHostPatterns = [
    /\/(image|img|photo|media|uploads|static)\//i,
    /\?.*format=(jpg|jpeg|png|gif|webp)/i,
    /blob\.core\.windows\.net.*\.(jpg|jpeg|png|gif|webp)/i,
    /cloudinary\.com/i,
    /imgur\.com/i,
    /googleusercontent\.com/i,
    /images\./i,
  ];

  return imageHostPatterns.some((pattern) => pattern.test(url));
};

// Helper function to get a website thumbnail/preview URL
export const getWebsitePreviewUrl = (url) => {
  const encodedUrl = encodeURIComponent(url);
  return `https://api.microlink.io/?url=${encodedUrl}&screenshot=true&meta=false&embed=screenshot.url`;
};

// Function to extract images from markdown and replace them with numbered links
export const extractAndReplaceImages = (text) => {
  if (!text || typeof text !== "string")
    return { text: text || "", images: [], webLinks: [] };

  const images = [];
  const webLinks = [];
  let imageCounter = 1;
  let linkCounter = 1;

  const imageRegex = /!\[(.*?)\]\((.*?)(?:\s+"(.*?)")?\)/g;

  let newText = text.replace(imageRegex, (match, alt, url, title) => {
    const cleanUrl = url.trim();

    if (isImageUrl(cleanUrl)) {
      const label = `Photo ${imageCounter++}`;
      images.push({
        src: cleanUrl,
        alt: alt,
        title: title,
        label: label,
        type: "image",
      });
      return `[${label} ](${cleanUrl})`;
    } else {
      const label = `Link ${linkCounter++}`;
      webLinks.push({
        src: cleanUrl,
        alt: alt,
        title: title,
        label: label,
        type: "weblink",
        previewUrl: getWebsitePreviewUrl(cleanUrl),
      });
      return `[${label} ](${cleanUrl})`;
    }
  });

  // Also extract plain URLs that are not in markdown format
  // eslint-disable-next-line no-useless-escape
  const plainUrlRegex =
    /https?:\/\/[\w-]+(\.[\w-]+)+(\/[\w\-.~:/?#[\]@!$&'()*+,;=%]*)?/g;

  const urlMatches = [...newText.matchAll(plainUrlRegex)];
  const processedUrls = new Set();

  webLinks.forEach((link) => processedUrls.add(link.src));
  images.forEach((img) => processedUrls.add(img.src));

  urlMatches.forEach((match) => {
    let url = match[0];
    url = url.replace(/[.,;:!?)\]]+$/, "");

    if (processedUrls.has(url) || isImageUrl(url)) return;

    const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const markdownLinkRegex = new RegExp(`\\]\\(${escapedUrl}`);
    if (markdownLinkRegex.test(newText)) return;

    processedUrls.add(url);

    const label = `Link ${linkCounter++}`;
    webLinks.push({
      src: url,
      alt: "",
      title: "",
      label: label,
      type: "weblink",
      previewUrl: getWebsitePreviewUrl(url),
    });
  });

  return { text: newText, images, webLinks };
};

// Replace placeholders with markdown links to the actual image URL
export const addImageLinksToText = (text, images) => {
  if (!text || !images || images.length === 0) return text;

  let linkedText = text;
  images.forEach((image) => {
    if (!image?.label || !image?.src) return;
    const escapedLabel = image.label.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
    const placeholderRegex = new RegExp(`\\[${escapedLabel}\\]`, "g");
    linkedText = linkedText.replace(
      placeholderRegex,
      `[${image.label}](${image.src})`,
    );
  });

  return linkedText;
};

// Extract a readable filename for display purposes
export const getImageDisplayName = (image) => {
  if (!image) return "";
  if (image.alt) return image.alt;
  if (image.title) return image.title;
  try {
    const urlObj = new URL(image.src);
    const pathname = urlObj.pathname.split("/").filter(Boolean);
    if (pathname.length) return pathname[pathname.length - 1];
  } catch (e) {
    if (image.src) {
      const parts = image.src.split("/").filter(Boolean);
      if (parts.length) return parts[parts.length - 1];
    }
  }
  return image.label || "Image";
};

// Extract block reference numbers from message content
// References appear as [1,2,3] or [1][2][3] in the answer
export const getUsedBlocks = (message) => {
  const usedBlocksSet = new Set();
  const regex = /\[((\d+)(,( )*\d+)*)\]/g;
  let match = regex.exec(message.content);
  while (match != null) {
    const blocks = match[1].split(",");
    blocks.forEach((block) => usedBlocksSet.add(+block));
    match = regex.exec(message.content);
  }
  return Array.from(usedBlocksSet);
};

// Helper: format bytes to MB string
export const formatFileSize = (sizeInBytes) => {
  if (!sizeInBytes && sizeInBytes !== 0) return "";
  const mb = sizeInBytes / 1024 / 1024;
  return `${mb.toFixed(2)} MB`;
};

// Helper: get file extension (uppercased)
export const getFileExtension = (fileName) => {
  if (!fileName) return "";
  const parts = fileName.split(".");
  if (parts.length === 1) return "";
  return parts[parts.length - 1].toUpperCase();
};
